import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { campaignService, donationService } from '../services';
import { resolveMediaUrl } from '../services/api';
import PageBreadcrumb from '../components/PageBreadcrumb';

const isDonationEligible = (project) => {
    if (!project) return true;
    if (project.status === 'ongoing' || project.status === 'pending') return true;
    if (project.start_date) return new Date(project.start_date) > new Date();
    return false;
};

const Donate = () => {
    const location = useLocation();
    const selectedProject = location.state?.project || null;
    const initialQuickAmount = location.state?.quickAmount || '';
    const [campaigns, setCampaigns] = useState([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [amount, setAmount] = useState('');
    const [customAmount, setCustomAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        donor_name: '',
        donor_email: '',
        donor_phone: '',
        payment_method: 'easypaisa',
        campaign_id: '',
        payment_screenshot: null,
    });

    useEffect(() => {
        fetchCampaigns();
    }, []);

    useEffect(() => {
        if (initialQuickAmount) {
            setAmount(String(initialQuickAmount));
            setCustomAmount('');
        }
    }, [initialQuickAmount]);

    const fetchCampaigns = async () => {
        try {
            const response = await campaignService.getAll();
            const list = response.data.data || [];
            setCampaigns(list);
            if (list.length > 0) {
                setFormData((prev) => ({ ...prev, campaign_id: String(list[0].id) }));
            }
        } catch (err) {
            console.error('Error fetching campaigns:', err);
        } finally {
            setLoadingCampaigns(false);
        }
    };

    const selectedCampaign = useMemo(
        () => campaigns.find((c) => String(c.id) === String(formData.campaign_id)),
        [campaigns, formData.campaign_id]
    );

    const handleDonationAmountClick = (value) => {
        setAmount(value);
        setCustomAmount('');
    };

    const handleCustomAmountChange = (e) => {
        setCustomAmount(e.target.value);
        setAmount('');
    };

    const handleFormChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'file' ? files?.[0] || null : value }));
    };

    const finalAmount = customAmount || amount;
    const canDonateForProject = isDonationEligible(selectedProject);

    const submitDonation = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!formData.campaign_id) {
            setError('Please select a campaign.');
            return;
        }

        if (!finalAmount || Number(finalAmount) <= 0) {
            setError('Please select a valid amount.');
            return;
        }
        if (!canDonateForProject) {
            setError('This project is not accepting donations.');
            return;
        }
        if ((formData.payment_method === 'easypaisa' || formData.payment_method === 'jazzcash') && !formData.payment_screenshot) {
            setError('Please upload payment screenshot for Easypaisa/JazzCash donation.');
            return;
        }

        try {
            setSubmitting(true);
            const payload = new FormData();
            payload.append('amount', String(Number(finalAmount)));
            payload.append('currency', 'PKR');
            payload.append('payment_method', formData.payment_method);
            payload.append('donor_name', formData.donor_name);
            payload.append('donor_email', formData.donor_email);
            payload.append('donor_phone', formData.donor_phone || '');
            payload.append('campaign_id', String(Number(formData.campaign_id)));
            if (formData.payment_screenshot) {
                payload.append('payment_screenshot', formData.payment_screenshot);
            }
            await donationService.create(payload);
            setMessage('Donation submitted successfully. Thank you for your support.');
            setAmount('');
            setCustomAmount('');
            setFormData((prev) => ({ ...prev, donor_name: '', donor_email: '', donor_phone: '', payment_screenshot: null }));
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to submit donation.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-gray-50">
            <PageBreadcrumb
                title="Support Our Cause"
                description="Donate to active campaigns managed from CMS"
                items={[{ label: 'Donate' }]}
            />

            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-3xl font-bold text-gray-800 mb-8">Make Your Donation</h2>
                            {selectedProject && (
                                <div className={`mb-4 rounded-lg p-3 ${canDonateForProject ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`}>
                                    {canDonateForProject
                                        ? `Donating for project: ${selectedProject.title}`
                                        : `Project "${selectedProject.title}" is not ongoing/upcoming, so donation is disabled.`}
                                </div>
                            )}
                            {message && <div className="mb-4 rounded-lg bg-green-100 p-3 text-green-700">{message}</div>}
                            {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-red-700">{error}</div>}

                            <form onSubmit={submitDonation} className="space-y-6">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Select Campaign</label>
                                    <select
                                        name="campaign_id"
                                        value={formData.campaign_id}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        required
                                    >
                                        {campaigns.map((campaign) => (
                                            <option key={campaign.id} value={campaign.id}>
                                                {campaign.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <p className="text-gray-700 font-semibold mb-4">Select Amount (PKR)</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        {['500', '1000', '2500', '5000'].map((value) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => handleDonationAmountClick(value)}
                                                className={`py-3 px-4 rounded-lg font-semibold transition ${amount === value ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                                    }`}
                                            >
                                                PKR {value}
                                            </button>
                                        ))}
                                    </div>
                                    <input
                                        type="number"
                                        value={customAmount}
                                        onChange={handleCustomAmountChange}
                                        placeholder="Or enter custom amount"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input
                                        type="text"
                                        name="donor_name"
                                        value={formData.donor_name}
                                        onChange={handleFormChange}
                                        required
                                        placeholder="Full Name"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                    />
                                    <input
                                        type="email"
                                        name="donor_email"
                                        value={formData.donor_email}
                                        onChange={handleFormChange}
                                        required
                                        placeholder="Email"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                    />
                                    <input
                                        type="tel"
                                        name="donor_phone"
                                        value={formData.donor_phone}
                                        onChange={handleFormChange}
                                        placeholder="Phone"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg md:col-span-2"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">Payment Method</label>
                                    <select
                                        name="payment_method"
                                        value={formData.payment_method}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                    >
                                        <option value="easypaisa">Easypaisa</option>
                                        <option value="jazzcash">JazzCash</option>
                                    </select>
                                </div>
                               
                                {(formData.payment_method === 'easypaisa' || formData.payment_method === 'jazzcash') && (
                                    <div>
                                        <label className="block text-gray-700 font-semibold mb-2">Payment Screenshot</label>
                                        <input
                                            type="file"
                                            name="payment_screenshot"
                                            accept="image/*"
                                            onChange={handleFormChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            required
                                        />
                                        <p className="mt-1 text-xs text-gray-500">Upload payment proof after sending via Easypaisa or JazzCash.</p>
                                    </div>
                                )}

                                <div className="bg-emerald-50 p-4 rounded-lg flex justify-between">
                                    <span className="text-gray-700">Donation Amount:</span>
                                    <span className="font-bold text-emerald-700">PKR {Number(finalAmount || 0).toLocaleString()}</span>
                                </div>

                                <button
                                    type="submit"
                                    disabled={!finalAmount || submitting || !canDonateForProject}
                                    className="w-full bg-emerald-600 text-white font-bold py-3 rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
                                >
                                    {submitting ? 'Submitting...' : 'Submit Donation'}
                                </button>
                            </form>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-gray-800">Active Campaigns</h2>
                        {loadingCampaigns ? (
                            <div className="text-center py-8">
                                <div className="inline-block h-10 w-10 animate-spin rounded-full border-b-2 border-emerald-600"></div>
                            </div>
                        ) : (
                            campaigns.map((campaign) => {
                                const goal = Number(campaign.goal_amount || 0);
                                const raised = Number(campaign.current_amount || 0);
                                const progress = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
                                const active = String(formData.campaign_id) === String(campaign.id);
                                return (
                                    <button
                                        type="button"
                                        key={campaign.id}
                                        onClick={() => setFormData((prev) => ({ ...prev, campaign_id: String(campaign.id) }))}
                                        className={`w-full text-left rounded-lg bg-white shadow-lg overflow-hidden border-2 ${active ? 'border-emerald-500' : 'border-transparent'}`}
                                    >
                                        {campaign.image && (
                                            <img src={resolveMediaUrl(campaign.image)} alt={campaign.title} className="h-36 w-full object-cover" />
                                        )}
                                        <div className="p-4">
                                            <h3 className="font-bold text-gray-800">{campaign.title}</h3>
                                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{campaign.description}</p>
                                            <div className="mt-3 h-2 rounded bg-gray-200">
                                                <div className="h-2 rounded bg-emerald-600" style={{ width: `${progress}%` }} />
                                            </div>
                                            <p className="mt-2 text-xs text-gray-600">Raised PKR {raised.toLocaleString()} / {goal.toLocaleString()}</p>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                        {!selectedCampaign && !loadingCampaigns && (
                            <p className="text-sm text-gray-600">No active campaign available right now.</p>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Donate;
